import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, Form as BootstrapForm, Modal, Alert } from "react-bootstrap";
import styles from "./Activities.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import useFetch from "hooks/useFetch";
import { Button, Header } from "stories";

interface Activity {
  id?: number;
  title?: string;
  imageUrl?: string;
  description?: string;
  date?: string;
  activated?: boolean;
}

const ActivitiesPage: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [postImagePreview, setPostImagePreview] = useState<string | null>(null);
  const [patchImagePreview, setPatchImagePreview] = useState<string | null>(
    null,
  );

  const { execute, isLoading } = useFetch();
  const { execute: fetchActivities } = useFetch<Activity[]>();
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<Activity | null>(
    null,
  );

  const handleDeleteClick = (activity: Activity) => {
    setActivityToDelete(activity);
    setShowDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setActivityToDelete(null);
    setShowDeleteModal(false);
  };

  const deleteActivity = async (id: number) => {
    try {
      await execute(`/api/v1/activities/${id}`, "DELETE");
      setActivities((prev) => prev.filter((activity) => activity.id !== id));
      setShowDeleteModal(false);
      alert("Aktivitet slettet!");
    } catch (error) {
      console.error("Error deleting activity:", error);
      alert("Noe gikk galt. Vennligst prøv igjen.");
    }
  };

  const initialValues = {
    image: null,
    title: "",
    text: "",
    startDate: "",
  };

  const validationSchema = Yup.object({
    image: Yup.mixed().required("Et bilde er påkrevd"),
    title: Yup.string()
      .required("Tittel er påkrevd")
      .max(100, "Tittelen kan ikke være lengre enn 100 tegn"),
    text: Yup.string()
      .required("Teksten er påkrevd")
      .max(500, "Teksten kan ikke være lengre enn 500 tegn"),
    startDate: Yup.date().required("Startdato er påkrevd"),
  });

  const patchValidationSchema = Yup.object({
    title: Yup.string().max(100, "Tittelen kan ikke være lengre enn 100 tegn"),
    text: Yup.string().max(500, "Teksten kan ikke være lengre enn 500 tegn"),
    startDate: Yup.date(),
    image: Yup.mixed().nullable(),
  });

  const fetchData = async () => {
    const response = await fetchActivities(`/api/v1/activities`, "GET");
    if (response) {
      setActivities(response);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setSelectedActivity(null);
    setShowModal(false);
    setPatchImagePreview(null);
  };

  const handlePostFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] || null;
    if (file) {
      setPostImagePreview(URL.createObjectURL(file));
    }
  };

  const handlePatchFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.currentTarget.files?.[0] || null;
    if (file) {
      setPatchImagePreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className={styles.activitiesPageContainer}>
      <Header title="Aktiviteter" />
      <br />
      <Card className={styles.activityCard}>
        <Card.Body>
          <h2 className="text-center">Opprett Ny Aktivitet</h2>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
              const formData = new FormData();

              const activity = {
                title: values.title,
                description: values.text,
                date: values.startDate,
              };

              formData.append("activity", JSON.stringify(activity));

              if (values.image) {
                formData.append("file", values.image);
              }

              try {
                const response = (await execute(
                  "/api/v1/activities",
                  "POST",
                  formData,
                )) as Activity;

                if (response) {
                  setActivities((prev) => [
                    ...prev,
                    {
                      title: response.title,
                      imageUrl: response.imageUrl,
                      description: response.description,
                      date: response.date,
                    },
                  ]);
                  resetForm();
                  setPostImagePreview(null);
                  fetchData();
                }
              } catch (error) {
                console.error("Error submitting activity:", error);
                alert("Noe gikk galt. Vennligst prøv igjen.");
              }
            }}
          >
            {({ setFieldValue, isSubmitting, isValid, dirty }) => (
              <Form>
                <BootstrapForm.Group
                  controlId="formImage"
                  className={styles.formGroup}
                >
                  <BootstrapForm.Label>Last opp et bilde*</BootstrapForm.Label>
                  {postImagePreview && (
                    <div className={styles.imagePreview}>
                      <img
                        src={postImagePreview}
                        alt="Preview"
                        className="img-fluid mb-3"
                        style={{
                          maxHeight: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                    </div>
                  )}

                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(event) => {
                      setFieldValue(
                        "image",
                        event.currentTarget.files?.[0] || null,
                      );
                      handlePostFileChange(event);
                    }}
                  />
                  <ErrorMessage
                    name="image"
                    component="div"
                    className="text-danger mt-1"
                  />
                </BootstrapForm.Group>

                <BootstrapForm.Group
                  controlId="formTitle"
                  className={styles.formGroup}
                >
                  <BootstrapForm.Label>Tittel*</BootstrapForm.Label>
                  <Field
                    type="text"
                    name="title"
                    className="form-control"
                    placeholder="Skriv aktivitetstittel"
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="text-danger mt-1"
                  />
                </BootstrapForm.Group>

                <BootstrapForm.Group
                  controlId="formText"
                  className={styles.formGroup}
                >
                  <BootstrapForm.Label>Tekst*</BootstrapForm.Label>
                  <Field
                    as="textarea"
                    name="text"
                    className="form-control"
                    rows={5}
                    placeholder="Skriv aktivitetstekst"
                  />
                  <ErrorMessage
                    name="text"
                    component="div"
                    className="text-danger mt-1"
                  />
                </BootstrapForm.Group>

                <BootstrapForm.Group
                  controlId="formStartDate"
                  className={styles.formGroup}
                >
                  <BootstrapForm.Label>Startdato*</BootstrapForm.Label>
                  <Field
                    type="date"
                    name="startDate"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="startDate"
                    component="div"
                    className="text-danger mt-1"
                  />
                </BootstrapForm.Group>

                <div className="text-center">
                  <Button
                    type="submit"
                    variant="primary"
                    className={styles.largeButton}
                    disabled={isSubmitting || !(isValid && dirty)}
                  >
                    {isSubmitting || isLoading
                      ? "Lagrer..."
                      : "Opprett Aktivitet"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>

      <div className={styles.activityList}>
        {activities.length === 0 && !isLoading && (
          <Alert variant="warning" className="text-center">
            Det finnes ingen aktiviteter for øyeblikket.
          </Alert>
        )}

        {activities.map((activity) => (
          <Card key={activity.id} className={styles.activityCard}>
            <Card.Img
              variant="top"
              src={activity.imageUrl}
              alt="Activity Image"
              className={styles.activityImage}
            />
            <Card.Body>
              <Card.Title>{activity.title}</Card.Title>
              <Card.Text>
                <strong>Startdato:</strong>{" "}
                {activity.date && new Date(activity.date).toLocaleDateString()}
              </Card.Text>
              <div className={styles.buttonContainer}>
                <Button
                  variant="secondary"
                  onClick={() => handleEditClick(activity)}
                >
                  <FontAwesomeIcon icon={faEdit} /> Rediger
                </Button>
                <Button
                  variant="error"
                  onClick={() => handleDeleteClick(activity)}
                >
                  <FontAwesomeIcon icon={faTrash} /> Slett
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}

        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Rediger Aktivitet</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedActivity && (
              <Formik
                initialValues={{
                  image: null,
                  title: selectedActivity.title || "",
                  text: selectedActivity.description || "",
                  startDate: selectedActivity.date || "",
                }}
                validationSchema={patchValidationSchema}
                onSubmit={async (values, { resetForm }) => {
                  const formData = new FormData();

                  const activity = {
                    title: values.title,
                    description: values.text,
                    date: values.startDate,
                  };

                  formData.append("activity", JSON.stringify(activity));

                  if (values.image) {
                    formData.append("file", values.image);
                  }

                  try {
                    const response = await execute(
                      `/api/v1/activities/${selectedActivity.id}`,
                      "PATCH",
                      formData,
                    );

                    if (response) {
                      setActivities((prev) =>
                        prev.map((activity) =>
                          activity.id === selectedActivity.id
                            ? { ...activity, ...response }
                            : activity,
                        ),
                      );
                      resetForm();
                      handleModalClose();
                      alert("Aktivitet oppdatert!");
                    }
                  } catch (error) {
                    console.error("Error updating activity:", error);
                    alert("Noe gikk galt. Vennligst prøv igjen.");
                  }
                }}
              >
                {({ setFieldValue, isSubmitting, dirty }) => (
                  <Form>
                    <BootstrapForm.Group
                      controlId="formImage"
                      className={styles.formGroup}
                    >
                      <BootstrapForm.Label>Endre bilde</BootstrapForm.Label>
                      {patchImagePreview ? (
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <img
                            src={patchImagePreview}
                            alt="Preview"
                            className="img-fluid mb-3"
                            style={{
                              maxHeight: "200px",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        </div>
                      ) : selectedActivity?.imageUrl ? (
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <img
                            src={selectedActivity.imageUrl}
                            alt="Existing Image"
                            className="img-fluid mb-3"
                            style={{
                              maxHeight: "200px",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        </div>
                      ) : null}

                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(event) => {
                          setFieldValue(
                            "image",
                            event.currentTarget.files?.[0] || null,
                          );
                          handlePatchFileChange(event);
                        }}
                      />
                      <ErrorMessage
                        name="image"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </BootstrapForm.Group>

                    <BootstrapForm.Group
                      controlId="formTitle"
                      className={styles.formGroup}
                    >
                      <BootstrapForm.Label>Tittel</BootstrapForm.Label>
                      <Field
                        type="text"
                        name="title"
                        className="form-control"
                        placeholder="Oppdater aktivitetstittel"
                      />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </BootstrapForm.Group>

                    <BootstrapForm.Group
                      controlId="formText"
                      className={styles.formGroup}
                    >
                      <BootstrapForm.Label>Tekst</BootstrapForm.Label>
                      <Field
                        as="textarea"
                        name="text"
                        className="form-control"
                        rows={5}
                      />
                      <ErrorMessage
                        name="text"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </BootstrapForm.Group>

                    <BootstrapForm.Group
                      controlId="formStartDate"
                      className={styles.formGroup}
                    >
                      <BootstrapForm.Label>Startdato</BootstrapForm.Label>
                      <Field
                        type="date"
                        name="startDate"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="startDate"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </BootstrapForm.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      className={styles.largeButton}
                      disabled={isSubmitting || !dirty}
                    >
                      {isSubmitting ? "Lagrer..." : "Lagre Endringer"}
                    </Button>
                  </Form>
                )}
              </Formik>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={showDeleteModal} onHide={handleDeleteModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Bekreft Sletting</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {activityToDelete && (
              <>
                <p>Er du sikker på at du vil slette denne aktiviteten?</p>
                <p>
                  <strong>{activityToDelete.title}</strong>
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteModalClose}>
              Avbryt
            </Button>
            <Button
              variant="error"
              onClick={() => {
                if (activityToDelete?.id) {
                  deleteActivity(activityToDelete.id);
                }
              }}
            >
              Slett
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default ActivitiesPage;
